'use client';

import { SvgChevrondown } from '@common/icon';
import { cn } from '@core/utils';
import { useRef, useState } from 'react';

import type { FaqItem, MedianIncomeYear } from '../constants';
import { MEDIAN_INCOME_BY_YEAR } from '../constants';

interface FaqListProps {
  year: MedianIncomeYear;
}

/**
 * ⑨ FAQ — 자주 묻는 질문. scrollHeight 기반 height + opacity 트랜지션으로 부드럽게 펼침.
 */
export const FaqList = ({ year }: FaqListProps) => {
  const { faqList, source } = MEDIAN_INCOME_BY_YEAR[year];

  return (
    <section className="flex flex-col gap-12 mobile-tablet:px-16">
      <h2 className="font-700 text-Gray-900 typo-h5 mobile-tablet:typo-h6">
        자주 묻는 질문
      </h2>
      <p className="text-Gray-700 typo-body4">
        중위소득·기초생활보장에 대한 핵심 질문 {faqList.length}가지를
        정리했습니다.
      </p>
      <div className="flex flex-col">
        {faqList.map((f, idx) => (
          <FaqRow key={f.q} index={idx} item={f} />
        ))}
      </div>
      <p className="text-Gray-600 typo-caption1">
        <strong className="font-700 text-Gray-700">출처:</strong> {source} /
        통계청 가계금융복지조사
      </p>
    </section>
  );
};

interface FaqRowProps {
  index: number;
  item: FaqItem;
}

const FaqRow = ({ index, item }: FaqRowProps) => {
  const [opened, setOpened] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const height = opened ? (contentRef.current?.scrollHeight ?? 0) : 0;

  return (
    <div className="border-b-1 border-Gray-100 last:border-b-0">
      <button
        type="button"
        aria-expanded={opened}
        onClick={() => setOpened((prev) => !prev)}
        className="flex w-full cursor-pointer items-center justify-between gap-12 bg-transparent py-16 text-left font-600 text-Gray-900 typo-body4"
      >
        <span>
          Q{index + 1}. {item.q}
        </span>
        <SvgChevrondown
          aria-hidden="true"
          className={cn(
            'size-16 shrink-0 text-Gray-500 transition-transform duration-200',
            opened && 'rotate-180',
          )}
        />
      </button>
      <div
        className="overflow-hidden transition-[height] duration-300"
        style={{ height }}
      >
        <div
          ref={contentRef}
          className={cn(
            'pb-16 text-Gray-700 transition-opacity duration-300 typo-body4',
            opened ? 'opacity-100' : 'opacity-0',
          )}
          dangerouslySetInnerHTML={{ __html: item.a }}
        />
      </div>
    </div>
  );
};
